/**
 * Оркестрация тура: когда запускать, что показывать, как фиксировать прогресс.
 *
 * Провайдер намеренно лёгкий — движок и запросы. Всё, что рисует подсказку, живёт в
 * `TourRunner` и подгружается лениво, только когда шаги действительно отобраны (UI-5).
 */

import {
  Suspense,
  createContext,
  lazy,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIsMutating } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../auth/useAuth';
import { config } from '../../../lib/config';
import { useQuota } from '../../mailbox/api';
import { useOnboardingState, useReplayTour, useReportProgress } from '../api';
import {
  clearLocalState,
  clearPending,
  readLocalState,
  recordStepLocally,
} from '../engine/localState';
import type { OnboardingContext as PredicateContext } from '../engine/predicates';
import { selectSteps } from '../engine/selectSteps';
import { tempmailMainManifest } from '../manifests';
import type { OnboardingStep } from '../manifests/schema';
import type { ProgressEvent, StepOutcome, TourOutcome } from '../types';
import { useOnboardingContext } from '../useOnboardingContext';

const TourRunner = lazy(() =>
  import('./TourRunner').then((module) => ({ default: module.TourRunner })),
);

/** Ключ сессии: за одну сессию запускается не более одного тура (FR-12). */
const SESSION_KEY = 'k1.onboarding.session';

export interface OnboardingApi {
  /** Ручной перезапуск тура из меню профиля (FR-11). */
  replay: () => void;
  /** Есть ли смысл показывать пункт «Показать подсказки». */
  isAvailable: boolean;
}

const OnboardingApiContext = createContext<OnboardingApi>({
  replay: () => undefined,
  isAvailable: false,
});

export function useOnboarding(): OnboardingApi {
  return useContext(OnboardingApiContext);
}

function sessionStarted(): boolean {
  try {
    return sessionStorage.getItem(SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

function markSessionStarted(): void {
  try {
    sessionStorage.setItem(SESSION_KEY, '1');
  } catch {
    // Приватный режим: ограничение «один тур на сессию» просто не применится.
  }
}

/**
 * FR-13: тур не влезает поверх открытой модалки, заполняемой формы или идущей мутации —
 * в такие моменты подсказка перекрыла бы то, чем пользователь занят прямо сейчас.
 */
function pageIsBusy(mutating: number): boolean {
  if (mutating > 0) return true;
  if (document.querySelector('[role="dialog"], [role="alertdialog"]')) return true;

  const active = document.activeElement;
  return active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement;
}

function whenIdle(callback: () => void): () => void {
  if (typeof requestIdleCallback === 'function') {
    const handle = requestIdleCallback(callback, { timeout: 2000 });
    return () => {
      cancelIdleCallback(handle);
    };
  }
  const timer = setTimeout(callback, 500);
  return () => {
    clearTimeout(timer);
  };
}

export function OnboardingProvider({ children }: { children: React.ReactNode }) {
  const manifest = tempmailMainManifest;
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const stateQuery = useOnboardingState(isAuthenticated);
  const { mutate: reportProgress } = useReportProgress();
  const { mutate: replayTour } = useReplayTour();
  const context = useOnboardingContext();
  const quota = useQuota(false);
  const mutating = useIsMutating();

  const [queue, setQueue] = useState<OnboardingStep[]>([]);
  const [index, setIndex] = useState(0);
  const completionReported = useRef(false);

  // Контекст пересобирается на каждом рендере, но эффект запуска должен зависеть только
  // от событий, а не от идентичности объекта, иначе idle-колбэк переназначался бы вечно.
  const contextRef = useRef<PredicateContext>(context);
  contextRef.current = context;

  const policy = stateQuery.data?.policy ?? null;
  const tourState = stateQuery.data?.tours.find((tour) => tour.tourId === manifest.tourId) ?? null;

  const send = useCallback(
    (events: ProgressEvent[], tourOutcome?: TourOutcome) => {
      if (events.length === 0 && !tourOutcome) return;
      reportProgress(
        {
          tourId: manifest.tourId,
          campaignId: manifest.campaignId,
          manifestVersion: manifest.version,
          appVersion: config.release,
          events,
          ...(tourOutcome ? { tourOutcome } : {}),
        },
        {
          onSuccess: () => {
            clearPending(manifest.tourId);
          },
        },
      );
    },
    [manifest.campaignId, manifest.tourId, manifest.version, reportProgress],
  );

  // FR-21: события, не дошедшие до сервера в прошлый раз, уходят при следующей загрузке.
  const flushed = useRef(false);
  useEffect(() => {
    if (!isAuthenticated || flushed.current) return;
    flushed.current = true;
    const pending = readLocalState(manifest.tourId)?.pending ?? [];
    if (pending.length > 0) send(pending);
  }, [isAuthenticated, manifest.tourId, send]);

  // Старт тура: после первой отрисовки, в простое, когда пользователь ничем не занят.
  useEffect(() => {
    if (!policy || queue.length > 0 || sessionStarted()) return;

    return whenIdle(() => {
      if (pageIsBusy(mutating)) return;

      const local = readLocalState(manifest.tourId);
      const result = selectSteps({
        manifest,
        state: tourState,
        policy,
        context: contextRef.current,
        localSeen: local?.seenSteps,
        appVersion: config.release,
      });

      if (result.steps.length > 0) {
        markSessionStarted();
        setQueue(result.steps);
        setIndex(0);
        return;
      }

      // FR-15: показывать нечего, но состояние стоит закрыть на текущей версии, иначе
      // сервер так и будет считать тур незавершённым.
      if (result.shouldMarkCompleted && !completionReported.current) {
        completionReported.current = true;
        send([], 'completed');
      }
    });
  }, [policy, tourState, queue.length, mutating, manifest, send]);

  const endTour = useCallback(() => {
    setQueue([]);
    setIndex(0);
  }, []);

  const record = useCallback(
    (step: OnboardingStep, outcome: StepOutcome, tourOutcome?: TourOutcome) => {
      const event: ProgressEvent = {
        stepId: step.id,
        rev: step.rev,
        outcome,
        at: new Date().toISOString(),
      };
      // Локальная отметка ставится до ответа сервера (FR-20): при обрыве сети шаг не
      // покажется заново, а событие уйдёт из очереди при следующей загрузке.
      const local = recordStepLocally({
        tourId: manifest.tourId,
        campaignId: manifest.campaignId,
        manifestVersion: manifest.version,
        event,
        ...(tourOutcome ? { tourOutcome } : {}),
      });
      send(local.pending, tourOutcome);
    },
    [manifest.campaignId, manifest.tourId, manifest.version, send],
  );

  const advance = useCallback(
    (outcome: StepOutcome) => {
      const step = queue.at(index);
      if (!step) return;

      const isLast = index === queue.length - 1;
      record(step, outcome, isLast ? 'completed' : undefined);
      if (isLast) endTour();
      else setIndex((value) => value + 1);
    },
    [queue, index, record, endTour],
  );

  const close = useCallback(() => {
    const step = queue.at(index);
    // FR-19: закрытие помечает просмотренными только шаги текущего прохода —
    // будущие новые шаги пользователь всё равно увидит.
    if (step) record(step, 'dismissed', 'dismissed');
    endTour();
  }, [queue, index, record, endTour]);

  const replay = useCallback(() => {
    if (!policy) return;
    clearLocalState(manifest.tourId);
    replayTour(
      {
        tourId: manifest.tourId,
        campaignId: manifest.campaignId,
        manifestVersion: manifest.version,
        appVersion: config.release,
        trigger: 'user',
      },
      {
        onSuccess: () => {
          // Ручной перезапуск проходит мимо правил повтора: пользователь явно попросил
          // показать подсказки заново, поэтому отбор идёт как для нового пользователя.
          const result = selectSteps({ manifest, state: null, policy, context: contextRef.current });
          markSessionStarted();
          setQueue(result.steps);
          setIndex(0);
        },
      },
    );
  }, [manifest, policy, replayTour]);

  const period = quota.data?.traffic.period ?? 'month';
  const interpolation = useMemo(
    () => ({
      limit: quota.data?.active.limit ?? 0,
      minutes: quota.data?.mailbox?.activeMinutes ?? 0,
      traffic: quota.data?.traffic.limitMb ?? 0,
      reset: t(`quota.periodReset.${period}`),
    }),
    [quota.data, period, t],
  );

  const api = useMemo<OnboardingApi>(
    () => ({ replay, isAvailable: policy?.enabled ?? false }),
    [replay, policy],
  );

  const step = queue.at(index);

  return (
    <OnboardingApiContext.Provider value={api}>
      {children}
      {step && (
        <Suspense fallback={null}>
          <TourRunner
            step={step}
            current={index + 1}
            total={queue.length}
            isLast={index === queue.length - 1}
            breakpoint={context.breakpoint}
            interpolation={interpolation}
            onAdvance={advance}
            onClose={close}
          />
        </Suspense>
      )}
    </OnboardingApiContext.Provider>
  );
}
